import { useState, useEffect } from "react";
import {PaginatedList} from '../../components/ui/Pagination';
import useServerPagination from '../../components/ui/useServerPagination';
import {
  Building2,
  Users,
  ArrowDownCircle,
  ArrowUpCircle,
  Layers,
  AlertTriangle,
} from "lucide-react";
import ProtectedRoute from "../Components/ProtectedRoute";

import AnimatedChart from "../Components/AnimatedChart";
import DashboardLayout from "../Layouts/DashboardLayout";
import KitchenOrderCard from "../Components/KitchenOrderCard";
import {Link} from 'react-router-dom';
import {useAuth} from '../Context/AuthContext';
import {canPage} from '../../components/ui/PermissionButton';
import ERPSetupChecklist from '../Components/ERPSetupChecklist';

export default function DashboardHome() {
  const [chartType, setChartType] = useState("revenue");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,setError]=useState('');
  const auth=useAuth();

  const [dynamicAlerts,setDynamicAlerts]=useState([]);
  const alertServer=useServerPagination({endpoint:'/dashboard/alerts',onData:setDynamicAlerts,pageSize:5});

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await fetch("/api/dashboard/?omit_alerts=true", {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "success") {
        setDashboard(data);
        setError('');
      } else throw Error(data.message||'Unable to load dashboard');
    } catch (err) {
      setError(err.message||'Unable to reach dashboard');
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-10">Loading...</div>
      </DashboardLayout>
    );
  }

  if(error||!dashboard)return <DashboardLayout><div role="alert" className="rounded-xl border border-red-400/20 p-6"><p>{error||'Dashboard data is unavailable.'}</p><button onClick={()=>{setLoading(true);loadDashboard();}} className="mt-4 rounded-lg bg-amber-400 px-4 py-2 text-black">Try again</button></div></DashboardLayout>;

  return (
    <ProtectedRoute permission="dashboard">
      <DashboardLayout>
        <div className="space-y-10">
          {auth?.user?.role==='super_admin'&&<ERPSetupChecklist/>}
          <section className="flex bg-white/5 p-4 rounded-2xl border border-white/10 flex-wrap gap-3">
            {[
              [Building2,'Outlets','/admindashboard/branches','branches.view'],
              [Users,'Staff','/admindashboard/access/permissions','staff.manage'],
              [ArrowDownCircle,'Stock movements','/admindashboard/inventory/inward-history','inventory.movement'],
              [Layers,'Inventory','/admindashboard/inventory','inventory.view'],
              [ArrowUpCircle,'Kitchen orders','/admindashboard/production/kitchen','kitchen.view'],
            ].filter(([, ,to,key])=>canPage(auth,key,to)).map(([Icon,label,to])=><Link key={to} to={to} className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/40 px-4 py-2 text-sm hover:bg-white/10"><Icon size={16}/>{label}</Link>)}
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {(dashboard.stats||[]).map((s) => (
              <KpiCard
                key={s.label}
                label={s.label}
                value={
                  s.label.includes("Revenue")
                    ? `₹${Number(s.value).toLocaleString()}`
                    : s.value
                }
              />
            ))}
          </section>

          <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FinanceCard
              title="GST Collected (MTD)"
              value={`₹${dashboard.finance.gst.toLocaleString()}`}
            />

            <FinanceCard
              title="Payments Received"
              value={`₹${dashboard.finance.payments.toLocaleString()}`}
            />

            <FinanceCard
              title="Pending Receivables"
              value={`₹${dashboard.finance.pending.toLocaleString()}`}
              danger
            />
          </section>

          <section className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            <ChartBox title="Revenue & Orders">
              <ChartHeader
                options={["revenue", "orders", "gst"].filter(key=>Array.isArray(dashboard.charts?.[key]))}
                active={chartType}
                setActive={setChartType}
              />
              <AnimatedChart type={chartType} charts={dashboard?.charts} />
            </ChartBox>

            {dashboard.charts?.topItems&&<ChartBox title="Top Selling Items">
              <AnimatedChart type="top-items" charts={dashboard?.charts} />
            </ChartBox>}

            {dashboard.charts?.productionVsSales&&<ChartBox title="Production vs Sales">
              <AnimatedChart
                type="production-vs-sales"
                charts={dashboard?.charts}
              />
            </ChartBox>}
          </section>

          <section className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h3 className="font-serif text-lg mb-4">Intelligent Alerts</h3>

            {dynamicAlerts.length === 0 && !alertServer.pagination.loading && !alertServer.pagination.error && (
              <div className="text-emerald-400 text-sm">
                No recorded stock alerts. Check operational reports for further exceptions.
              </div>
            )}
              <PaginatedList items={dynamicAlerts} pagination={alertServer.pagination} label="Alerts" pageSize={5} className="space-y-3" renderItem={(a, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm
            ${
              a.type === "danger"
                ? "bg-red-500/10 text-red-400"
                : "bg-amber-400/10 text-amber-400"
            }`}
                  >
                    <AlertTriangle size={16} />
                    {a.text}
                  </div>
                )}/>
          </section>

          <section className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
              <h3 className="font-serif text-lg mb-4">Recent Activity</h3>
              <ul className="space-y-3 text-sm text-gray-300">
                {(dashboard.activities||[]).map((a, i) => (
                  <li key={i}>• {a.text}</li>
                ))}
              </ul>
            </div>

            <div className="xl:col-span-2">
              <h3 className="font-serif text-lg mb-4">Today's Kitchen Tickets</h3>
              {!dashboard.kitchen_orders?.length&&<p className="text-sm text-gray-400">No pending or prepared kitchen tickets recorded today.</p>}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {(dashboard.kitchen_orders||[]).map((o) => (
                  <KitchenOrderCard key={o.id} order={o} />
                ))}
              </div>
            </div>
          </section>
        </div>
      </DashboardLayout>
    </ProtectedRoute>
  );
}

function ChartBox({ title, children }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-8">
      <h3 className="font-serif text-lg mb-4">{title}</h3>
      {children}
    </div>
  );
}

function ChartHeader({ options, active, setActive }) {
  return (
    <div className="flex gap-2 mb-4">
      {options.map((t) => (
        <button
          key={t}
          onClick={() => setActive(t)}
          className={`px-4 py-1.5 text-xs rounded-lg transition
            ${
              active === t
                ? "bg-amber-400 text-black font-semibold"
                : "bg-black/40 text-gray-400 hover:text-white"
            }`}
        >
          {t.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function KpiCard({ label, value }) {
  return (
    <div className="relative rounded-2xl bg-white/5 border border-white/10 p-6 hover:border-amber-400/40 transition">
      <p className="text-xs text-gray-400 tracking-widest uppercase">{label}</p>
      <h2 className="mt-3 text-3xl font-serif">{value}</h2>
    </div>
  );
}

function FinanceCard({ title, value, danger }) {
  return (
    <div
      className={`rounded-2xl p-6 border ${
        danger
          ? "bg-red-500/10 border-red-500/30"
          : "bg-white/5 border-white/10"
      }`}
    >
      <p className="text-xs text-gray-400 tracking-widest uppercase">{title}</p>
      <h3 className={`mt-3 text-2xl font-serif ${danger && "text-red-400"}`}>
        {value}
      </h3>
    </div>
  );
}
